import type { CompositeLayer } from "../../../containers/SpecsEditor/compositorEngine";
import { LayerModel } from "../../../containers/SpecsEditor/specsModel/LayerModel";
import type {
  SpecsEditorNav,
  SpecsEditorSession,
  SpecsLayer,
  SpecsLayerView,
} from "../../../containers/SpecsEditor/types";
import { SPECULOOS_NAV_KIND } from "../../../containers/SpecsEditor/types";
import { getScopedLayers, getScopedVisibleLayers } from "./helpers";
import {
  getOutputBackPreviewUrl,
  getOutputPreviewUrl,
} from "./reducers/images";

type RootState = { specsEditor: SpecsEditorSession };

function runtimeLayersToView(state: SpecsEditorSession): SpecsLayer[] {
  return Object.values(state.layerRuntimeByKey).map((layer) => ({
    id: layer.key,
    name: layer.modelLayerPath.layerName ?? layer.key,
    visible: layer.visible,
    baseColor: layer.baseColor,
    zIndex: layer.zIndex,
    modelLayerPath: layer.modelLayerPath,
    highlight: layer.highlight,
    mask: layer.mask,
  }));
}

export function selectSpecsEditorNav(state: RootState): SpecsEditorNav {
  return state.specsEditor.nav;
}

export function selectRightPanelKind(
  state: RootState
):
  | "empty"
  | "settings"
  | "history"
  | "positions"
  | "parts"
  | "elements"
  | "styles"
  | "layerSpecs"
  | "shipping"
  | "metadata"
  | "global"
  | "families"
  | "fabrics"
  | "decorations"
  | "import"
  | "familiesHistory"
  | "fabricsHistory"
  | "decorationsHistory"
  | "layer" {
  const nav = selectSpecsEditorNav(state);
  return nav.kind === SPECULOOS_NAV_KIND.LAYER ||
    nav.kind === SPECULOOS_NAV_KIND.ELEMENT
    ? SPECULOOS_NAV_KIND.LAYER
    : nav.kind;
}

export function selectActiveStyleName(state: RootState): string | null {
  const nav = selectSpecsEditorNav(state);
  if (
    nav.kind !== SPECULOOS_NAV_KIND.ELEMENT &&
    nav.kind !== SPECULOOS_NAV_KIND.LAYER &&
    nav.kind !== SPECULOOS_NAV_KIND.LAYER_SPECS &&
    nav.kind !== SPECULOOS_NAV_KIND.POSITIONS
  ) {
    return null;
  }
  return nav.style ?? null;
}

export function selectVisibleLayers(state: RootState): SpecsLayer[] {
  const s = state.specsEditor;
  const nav = selectSpecsEditorNav(state);
  if (
    nav.kind === SPECULOOS_NAV_KIND.ELEMENT ||
    nav.kind === SPECULOOS_NAV_KIND.LAYER ||
    nav.kind === SPECULOOS_NAV_KIND.LAYER_SPECS ||
    (nav.kind === SPECULOOS_NAV_KIND.POSITIONS &&
      nav.part &&
      nav.element &&
      nav.style)
  ) {
    return getScopedVisibleLayers(s, nav.part!, nav.element!, nav.style!);
  }
  return runtimeLayersToView(s).filter((layer) => layer.visible);
}

export function selectCompositeLayers(state: RootState): CompositeLayer[] {
  return selectVisibleLayers(state).map((layer) => ({
    baseColor: LayerModel.isInheritedColor(layer.baseColor)
      ? state.specsEditor.previewColor
      : layer.baseColor,
    maskUrl: getOutputPreviewUrl(layer.mask),
    highlightUrl: getOutputPreviewUrl(layer.highlight),
  }));
}

export function selectBackCompositeLayers(state: RootState): CompositeLayer[] {
  return selectVisibleLayers(state).map((layer) => ({
    baseColor: LayerModel.isInheritedColor(layer.baseColor)
      ? state.specsEditor.previewColor
      : layer.baseColor,
    maskUrl: getOutputBackPreviewUrl(layer.mask),
    highlightUrl: getOutputBackPreviewUrl(layer.highlight),
  }));
}

export function selectSelectedLayer(state: RootState): SpecsLayer | null {
  const s = state.specsEditor;
  const nav = selectSpecsEditorNav(state);
  if (nav.kind === SPECULOOS_NAV_KIND.LAYER) {
    return (
      runtimeLayersToView(s).find((layer) => layer.id === nav.layerId) ?? null
    );
  }
  if (nav.kind === SPECULOOS_NAV_KIND.LAYER_SPECS) {
    if (nav.part && nav.element && nav.style) {
      return (
        getScopedVisibleLayers(s, nav.part, nav.element, nav.style).find(
          (layer) =>
            !nav.layerName || layer.modelLayerPath?.layerName === nav.layerName
        ) ??
        getScopedLayers(s, nav.part, nav.element, nav.style).find(
          (layer) =>
            !nav.layerName || layer.modelLayerPath?.layerName === nav.layerName
        ) ??
        null
      );
    }
    return (
      runtimeLayersToView(s).find((layer) => layer.visible) ??
      runtimeLayersToView(s)[0] ??
      null
    );
  }
  if (
    nav.kind === SPECULOOS_NAV_KIND.POSITIONS &&
    nav.part &&
    nav.element &&
    nav.style
  ) {
    const scopedLayer = nav.layerId
      ? (getScopedLayers(s, nav.part, nav.element, nav.style).find(
          (layer) => layer.id === nav.layerId
        ) ?? null)
      : null;
    return (
      scopedLayer ??
      getScopedVisibleLayers(s, nav.part, nav.element, nav.style)[0] ??
      getScopedLayers(s, nav.part, nav.element, nav.style)[0] ??
      null
    );
  }
  if (nav.kind === SPECULOOS_NAV_KIND.ELEMENT) {
    return (
      getScopedVisibleLayers(s, nav.part, nav.element, nav.style)[0] ??
      getScopedLayers(s, nav.part, nav.element, nav.style)[0] ??
      null
    );
  }
  return null;
}

export function selectHoveredMaskUrl(state: RootState): string | null {
  const s = state.specsEditor;
  if (!s.hoveredLayerId) return null;
  const layer = s.layerRuntimeByKey[s.hoveredLayerId];
  return layer?.mask?.outputImageUrl ?? null;
}

export function selectEffectiveLayerView(state: RootState): SpecsLayerView {
  return state.specsEditor.layerView;
}
